/**
 * Traduction des énumérations de la base en langage de salon.
 *
 * Centralisé pour une raison précise : « cancelled_by_salon » ne doit jamais
 * fuir dans une interface, et le vocabulaire doit être IDENTIQUE sur les trois
 * surfaces. Une employée qui lit « Absente » sur mobile et « No-show » sur
 * desktop doute de l'outil.
 */

export type Tone = "neutral" | "accent" | "success" | "warning" | "danger" | "muted";

export type StatusMeta = {
  label: string;
  tone: Tone;
  /** true ⇒ occupe le planning (utile pour griser une ligne annulée). */
  blocking: boolean;
};

export type AppointmentStatus =
  | "requested"
  | "confirmed"
  | "arrived"
  | "in_progress"
  | "completed"
  | "cancelled_by_client"
  | "cancelled_by_salon"
  | "no_show";

export const appointmentStatusMeta: Record<AppointmentStatus, StatusMeta> = {
  // « À confirmer » et non « En attente » : c'est une action pour l'équipe.
  requested: { label: "À confirmer", tone: "warning", blocking: true },
  confirmed: { label: "Confirmé", tone: "neutral", blocking: true },
  arrived: { label: "Arrivée", tone: "accent", blocking: true },
  in_progress: { label: "En cours", tone: "accent", blocking: true },
  completed: { label: "Terminé", tone: "success", blocking: true },
  cancelled_by_client: { label: "Annulé", tone: "muted", blocking: false },
  cancelled_by_salon: { label: "Annulé par le salon", tone: "muted", blocking: false },
  // « Absente » plutôt que « No-show » : on parle français, sans juger.
  no_show: { label: "Absente", tone: "danger", blocking: false },
};

export type CommunicationChannel =
  | "instagram"
  | "whatsapp"
  | "sms"
  | "email"
  | "web_chat"
  | "phone"
  | "internal_note"
  | "google_review";

export const channelMeta: Record<CommunicationChannel, { label: string; tone: Tone }> = {
  instagram: { label: "Instagram", tone: "accent" },
  whatsapp: { label: "WhatsApp", tone: "success" },
  sms: { label: "SMS", tone: "neutral" },
  email: { label: "E-mail", tone: "neutral" },
  web_chat: { label: "Chat du site", tone: "neutral" },
  phone: { label: "Téléphone", tone: "neutral" },
  internal_note: { label: "Note interne", tone: "muted" },
  google_review: { label: "Avis Google", tone: "warning" },
};

/**
 * Prochaine action naturelle sur un RDV. Sert à afficher UN bouton principal
 * plutôt qu'un menu : Nina n'a jamais à choisir dans une liste.
 */
export function nextAppointmentAction(
  status: AppointmentStatus,
): { to: AppointmentStatus; label: string } | null {
  switch (status) {
    case "requested":
      return { to: "confirmed", label: "Confirmer" };
    case "confirmed":
      return { to: "arrived", label: "Elle est arrivée" };
    case "arrived":
      return { to: "in_progress", label: "Je commence" };
    case "in_progress":
      return { to: "completed", label: "Terminé" };
    default:
      return null;
  }
}

/** Classes Tailwind par tonalité — un seul endroit à corriger. */
export const toneClasses: Record<Tone, string> = {
  neutral: "bg-creme-200 text-encre-700",
  accent: "bg-blush-100 text-blush-700",
  success: "bg-sauge-100 text-sauge-700",
  warning: "bg-ambre-100 text-ambre-700",
  danger: "bg-grenat-100 text-grenat-700",
  muted: "bg-creme-100 text-encre-400",
};
